import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Box,
  Stack,
  Typography,
  Link,
  SelectChangeEvent,
  MenuItem,
  Select,
  FormControlLabel,
  Checkbox,
  Button,
  Tooltip,
  useTheme,
  Chip,
  OutlinedInput,
  InputAdornment,
  Menu,
  styled,
  IconButton,
} from "@mui/material";
import { ObjectLayer } from "../../Core/Api/Types/object-layer";
import { DbAssetObjectInfos } from "../../Core/Api/Types/db-asset-object-infos";
import DataTable, { CellSize } from "../Table/table";
import { assetService, cacheService, searchService, thumbnailService, userService } from "../../Core/Api/services";
import { GetEnumValueLabelMap, GetObjectStatusLabel, OpenLinkInNewTab, UserHasAcl } from "../../Utils/helpers";
import TableCellAdamStatusHistory from "../Table/TableCellAdamStatusHistory/table-cell-adam-status-history";
import { routePaths } from "../../Router/router";
import { ObjectStatus } from "../../Core/Api/Types/object-status";
import AssetStatus from "../Misc/Status/AssetStatus/asset-status";
import { CreateNewDbMetadataValue, GetMetadataById, GetMetadataByTagName } from "../../Core/Utils/asset-object-utils";
import {
  GridActionsCellItem,
  GridColDef,
  GridRowParams,
  GridRowSelectionModel,
  GridSortModel,
  MuiEvent,
} from "@mui/x-data-grid";
import { useDispatch } from "react-redux";
import {
  AddOrUpdateFilter,
  AddOrUpdateStatusHistoryFilter,
  GetFilters,
  GetStatusHistoryFilter,
  RemoveAllFilters,
  RemoveFilterById,
  RemoveStatusHistoryFilter,
  StatusHistoryFilter,
  StoreFiltersState,
} from "../../Redux/Reducers/redux-slice-filters";
import { ComputeMetadataBaseType } from "../../Core/Utils/functions";
import { BaseMetadataType } from "../../Core/Api/Types/base-metadata-type";
import { IDbBaseMetadataValueInfos } from "../../Core/Api/Types/i-db-base-metadata-value-infos";
import AdamChannelsPageFilters from "./adam-channels-page-filters";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ClassIcon from "@mui/icons-material/Class";
import { MetadataFieldValueObjectTypes } from "../../Core/Api/Types/metadata-field-value-object-types";
import FiltersList from "../Filters/FiltersList/filters-list";
import { Link as RouterLink, useOutletContext } from "react-router";
import { DateRangePicker } from "@mui/x-date-pickers-pro/DateRangePicker";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { SingleInputDateRangeField } from "@mui/x-date-pickers-pro/SingleInputDateRangeField";
import { PickersShortcutsItem } from "@mui/x-date-pickers";
import { DateRange, LocalizationProvider } from "@mui/x-date-pickers-pro";
import { useSelector } from "react-redux";
import PlaceIcon from "@mui/icons-material/Place";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import TableCellDateTime from "../Table/TableCellDateTime/table-cell-date-time";
import SearchIcon from "@mui/icons-material/Search";
import moment from "moment";

import TroubleshootIcon from "@mui/icons-material/Troubleshoot";
import EmptyListSlot from "../Misc/EmptyListSlot";
import CollectionsBookmarkOutlinedIcon from "@mui/icons-material/CollectionsBookmarkOutlined";
import { CloseOutlined, ErrorOutline } from "@mui/icons-material";
import ErrorIcon from "../Misc/ErrorIcon";
import useCustomSnackbar from "../../Utils/Hooks/useCustomSnackbar";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import { ObjectType } from "../../Core/Api/Types/object-type";
import { AclTypes } from "../../Core/Api/Types/acl-types";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { ModalContext } from "../Modals";
import ModalSetFollowers, { ModalSetFollowersElementProps } from "../Modals/ModalSetFollowers/modal-set-followers";
import { ChangeStatusRequest } from "../../Core/Api/Types/change-status-request";
import ModalConfirmAction, { ModalConfirmActionElementProps } from "../Modals/ModalConfirmAction/modal-confirm-action";
import { DbObjectCreateOrUpdateInfos } from "../../Core/Api/Types/db-object-create-or-update-infos";
import { FollowerDto } from "../../Core/Api/Types/get-grouped-users-list-basic-response";
import { UpdateMetadataFieldsRequest } from "../../Core/Api/Types/update-metadata-fields-request";
import updateLocale from "dayjs/plugin/updateLocale";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import TableCellTypography from "../Table/TableCellTypography/table-cell-typography";
import TableCellAdamObjectInfos from "../Table/TableCellAdamObjectInfos/table-cell-adam-object-infos";
import { SearchRequestNative } from "../../Core/Api/Types/search-request-native";
import { ObjectClass } from "../../Core/Api/Types/object-class";
import TableCellStringExpend from "../Table/TableCellStringExpend/table-cell-string-expend";
import GlobalConst from "../../Core/Utils/Constants/globals";
import { CacheResults } from "../../Redux/Reducers/redux-slice-asset";
import { SortingType } from "../../Core/Api/Types/sorting-type";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import TableUuids from "../Table/table-uuids";
import { TableCellAdamVignette } from "../Table/TableCellAdamVignette/TableCellAdamVignette";
import useAdamDateFilterLinkValues from "../../Utils/Hooks/useAdamDateFilterLinkValues";
import { DbMetadataFieldInfos } from "../../Core/Api/Types/db-metadata-field-infos";
import { Dispatch, UnknownAction } from "@reduxjs/toolkit";
import "./date-picker.css";
dayjs.extend(updateLocale);

// Replace "en" with the name of the locale you want to update.
dayjs.updateLocale("en", {
  // Sunday = 0, Monday = 1.
  weekStart: 6,
});

export const handleAddDateFilter = (
  dates: any,
  filterDateType: string,
  dispatch: Dispatch<UnknownAction>,
  setLinkedDateFilterId: React.Dispatch<React.SetStateAction<number | null>>,
  today: dayjs.Dayjs,
  progDateDiff?: DbMetadataFieldInfos,
  dateLivraison?: DbMetadataFieldInfos,
  dateDiff?: DbMetadataFieldInfos,
  dateRightsStart?: DbMetadataFieldInfos,
  dateRightsEnd?: DbMetadataFieldInfos,
  setLinkedStatusHistoryFilter?: React.Dispatch<React.SetStateAction<ObjectStatus | null>>
) => {
  if (!dates || dates.length !== 2) return;
  const parsedDates = parseDates(dates);
  if (
    ["NeedCheckOrEditing", "Accepted", "AcceptedForBroadcast", "Delivered", "Broadcasted", "ingest"].includes(
      filterDateType
    )
  ) {
    dispatch(
      AddOrUpdateStatusHistoryFilter({
        startDate: parsedDates[0] ? new Date(parsedDates[0]) : undefined,
        endDate: parsedDates[1] ? new Date(parsedDates[1]) : undefined,
        status: filterDateType === "ingest" ? ObjectStatus.ParsedAndProbbed : (filterDateType as ObjectStatus),
      })
    );

    setLinkedDateFilterId(null);
    setLinkedStatusHistoryFilter && setLinkedStatusHistoryFilter(filterDateType as ObjectStatus);
    return;
  }

  let selectedMeta: any;

  if (filterDateType === "rc_prg_datesheures_diffs") selectedMeta = progDateDiff;
  if (filterDateType === "rc_ba_dateheure_livraison") selectedMeta = dateLivraison;
  if (filterDateType === "rc_ba_date_diff_ba") selectedMeta = dateDiff;
  if (filterDateType === "rc_ba_droits_debut") selectedMeta = dateRightsStart;
  if (filterDateType === "rc_ba_droits_fin") selectedMeta = dateRightsEnd;
  if (!selectedMeta) return;

  const metadataValues: IDbBaseMetadataValueInfos[] = [
    {
      $type: ComputeMetadataBaseType(BaseMetadataType.DbDateTimeMetadataValueInfos),
      id: selectedMeta.id,
      fieldType: BaseMetadataType.DbDateTimeMetadataValueInfos,
      value: new Date(parsedDates[0] ?? today.startOf("day")),
      creationTime: new Date().toISOString(),
      updateTime: null,
      fieldName: null,
      fieldLabel: null,
      targetLayer: ObjectLayer.Asset,
      valueLabel: "startDate",
    },
    {
      $type: ComputeMetadataBaseType(BaseMetadataType.DbDateTimeMetadataValueInfos),
      id: selectedMeta.id,
      fieldType: BaseMetadataType.DbDateTimeMetadataValueInfos,
      value: new Date(parsedDates[1] ?? today.add(365, "day").endOf("day")),
      creationTime: new Date().toISOString(),
      updateTime: null,
      fieldName: null,
      fieldLabel: null,
      targetLayer: ObjectLayer.Asset,
      valueLabel: "endDate",
    },
  ];

  if (parsedDates[0]) dispatch(AddOrUpdateFilter(metadataValues[0]));
  if (parsedDates[1]) dispatch(AddOrUpdateFilter(metadataValues[1]));

  setLinkedDateFilterId(selectedMeta.id);
  setLinkedStatusHistoryFilter && setLinkedStatusHistoryFilter(null);
};

// Tools
export const handleAR = (
  asset: DbAssetObjectInfos | null,
  techWId: number,
  setCurrentWidth: React.Dispatch<React.SetStateAction<number>>,
  techHId: number,
  setCurrentHeight: React.Dispatch<React.SetStateAction<number>>
) => {
  const techW = GetMetadataById(asset, techWId);
  const techH = GetMetadataById(asset, techHId);
  if (techW && techH && techW.value && techW.value > 0 && techH.value && techH.value > 0) {
    setCurrentWidth(techW.value);
    setCurrentHeight(techH.value);
  } else {
    setCurrentWidth(16);
    setCurrentHeight(9);
  }
};
export const getStatusHisory = (statusHistory) => {
  return (
    [...statusHistory].sort((a, b) => ((a.date ?? 0) < (b.date ?? 0) ? -1 : (a.date ?? 0) === (b.date ?? 0) ? 0 : 1)) ??
    []
  );
};

export const getSpliceFromIndex = (statusHistory) => {
  return statusHistory.findLastIndex(
    (x) => x.status === ObjectStatus.RejectedAuto || x.status === ObjectStatus.NeedCheckOrEditing
  );
};

export const getObjectStatus = (spliceFromIndex, statusHistory) => {
  return spliceFromIndex > 0 ? [...statusHistory.slice(spliceFromIndex, statusHistory.length)] : [...statusHistory];
};

export const parseDates = (dates: any) => [
  dates[0] ? dates[0].startOf("day") : null,
  dates[1] ? dates[1].endOf("day") : null,
];

export const CHANNELS_VALUE_LABEL = [
  { value: "F2", label: "France 2", miniLabel: "F2" },
  { value: "F3", label: "France 3", miniLabel: "F3" },
  { value: "F4", label: "France 4", miniLabel: "F4" },
  { value: "F5", label: "France 5", miniLabel: "F5" },
  { value: "OKOO_F3", label: "Okoo F3", miniLabel: "OF3" },
  { value: "OKOO_F4", label: "Okoo F4", miniLabel: "OF4" },
  { value: "OKOO_F5", label: "Okoo F5", miniLabel: "OF5" },
  { value: "FG", label: "FTV", miniLabel: "FTV" },
  { value: "SL", label: "Slash", miniLabel: "Sl" },
  { value: "LU", label: "Lumni", miniLabel: "LU" },
];

export const PROVENANCE_VALUE_LABEL = [
  { value: "SHERLOCK", label: "Sherlock" },
  { value: "OSCAR", label: "Oscar" },
  { value: "GSA", label: "GSA" },
];

export const LIB_PROJECT_VALUE_LABEL = [
  { value: "BAN", label: "Bande-Annonce" },
  { value: "TDS", label: "Tout de suite" },
  { value: "SOM", label: "Sommaire" },
  { value: "HAB", label: "Habillage" },
  { value: "CLI", label: "Clip" },
  { value: "EHA", label: "EVS" },
  { value: "NUM", label: "Numérique" },
];

export const shortcutsItems: PickersShortcutsItem<any>[] = [
  {
    label: "Aujourd'hui",
    getValue: () => {
      const today = dayjs();
      return [today.startOf("day"), today.endOf("day")];
    },
  },
  {
    label: "Semaine -2",
    getValue: () => {
      const today = dayjs();
      const nextWeek = today.add(-14, "day");
      return [nextWeek.startOf("week"), nextWeek.endOf("week")];
    },
  },
  {
    label: "Semaine -1",
    getValue: () => {
      const today = dayjs();
      const nextWeek = today.add(-7, "day");
      return [nextWeek.startOf("week"), nextWeek.endOf("week")];
    },
  },
  {
    label: "Cette semaine",
    getValue: () => {
      const today = dayjs();
      return [today.startOf("week"), today.endOf("week")];
    },
  },
  {
    label: "Semaine prochaine",
    getValue: () => {
      const today = dayjs();
      const nextWeek = today.add(7, "day");
      return [nextWeek.startOf("week"), nextWeek.endOf("week")];
    },
  },
  {
    label: "Semaine +2",
    getValue: () => {
      const today = dayjs();
      const nextWeek = today.add(14, "day");
      return [nextWeek.startOf("week"), nextWeek.endOf("week")];
    },
  },
  {
    label: "Semaine +3",
    getValue: () => {
      const today = dayjs();
      const nextWeek = today.add(21, "day");
      return [nextWeek.startOf("week"), nextWeek.endOf("week")];
    },
  },
  {
    label: "30 prochains jours",
    getValue: () => {
      const today = dayjs();
      return [today, today.add(30, "day")];
    },
  },
  {
    label: "Ce mois",
    getValue: () => {
      const today = dayjs();
      return [today.startOf("month"), today.endOf("month")];
    },
  },
  {
    label: "Mois prochain",
    getValue: () => {
      const today = dayjs();
      const startOfNextMonth = today.endOf("month").add(1, "day");
      return [startOfNextMonth, startOfNextMonth.endOf("month")];
    },
  },
  {
    label: "Prochain 12 mois",
    getValue: () => {
      const today = dayjs();
      const endDate = today.add(12, "month");
      return [today, endDate];
    },
  },
];

interface AssetDeliveryProps {}
const page = "adamTrailers";

const AdamTrailers: FC<AssetDeliveryProps> = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const apiGrid = useGridApiRef();
  const theme = useTheme();

  const [isLoading, setIsLoading] = React.useState(false);
  const [rows, setRows] = React.useState<DbAssetObjectInfos[]>([]);
  const [error, setError] = React.useState<boolean>(false);
  const [noResult, setNoResult] = React.useState<boolean>(false);
  const [searchPattern, setSearchPattern] = React.useState<string>("");
  const [filterDateType, setFilterDateType] = React.useState<string>("rc_ba_dateheure_livraison");
  const [linkedDateFilterId, setLinkedDateFilterId] = React.useState<number | null>(null);
  const [linkedStatusHistoryFilter, setLinkedStatusHistoryFilter] = React.useState<ObjectStatus | null>(null);
  const [searchExpanded, setSearchExpanded] = React.useState<boolean>(true);
  const [searchChange, setSearchChange] = React.useState<boolean>(false);
  const [isFetchingNewPage, setIsFetchingNewPage] = React.useState<boolean>(false);

  const today = dayjs();

  const [dateValue, setDateValue] = React.useState<DateRange<Dayjs>>([null, null]);
  const [filterToDeliver, setFilterToDeliver] = React.useState<boolean>(false);
  const [filterAssignedToMe, setFilterAssignedToMe] = React.useState<boolean>(false);
  const [followersUsersOptions, setFollowersUsersOptions] = useState<FollowerDto[]>([]);
  const [tableSorting, setTableSorting] = React.useState<GridSortModel>(
    cacheService.GetTableUserSetup(TableUuids.ADAM_TRAILERS).columnsSort ?? []
  );

  const [count, setCount] = useState(0);
  const metadatas = cacheService.GetMetadata();

  const channelMeta = useMemo(() => metadatas.find((x) => x.tagName === "rc_ba_lib_chaine"), [metadatas]);
  const channelChildMeta = useMemo(() => metadatas.find((x) => x.tagName === "rc_ba_lib_chaine_enfant"), [metadatas]);
  const typeMeta = useMemo(() => metadatas.find((x) => x.tagName === "rc_ba_lib_projet"), [metadatas]);
  const dateLivraison = useMemo(() => metadatas.find((x) => x.tagName === "rc_ba_dateheure_livraison"), [metadatas]);
  const dateDiff = useMemo(() => metadatas.find((x) => x.tagName === "rc_ba_date_diff_ba"), [metadatas]);
  const dateRightsStart = useMemo(() => metadatas.find((x) => x.tagName === "rc_ba_droits_debut"), [metadatas]);
  const dateRightsEnd = useMemo(() => metadatas.find((x) => x.tagName === "rc_ba_droits_fin"), [metadatas]);
  const hiddenMeta = useMemo(() => metadatas.find((x) => x.tagName === "rc_ba_masquer"), [metadatas]);
  const provenanceMeta = useMemo(() => metadatas.find((x) => x.tagName === "rc_ba_provenance"), [metadatas]);
  const adamTypeMeta = useMemo(() => metadatas.find((x) => x.tagName === "adamAssetType"), [metadatas]);

  const techWidthMeta = useMemo(() => metadatas.find((x) => x.tagName === "techWidth"), [metadatas]);
  const techHeightMeta = useMemo(() => metadatas.find((x) => x.tagName === "techHeight"), [metadatas]);

  const [currentWidth, setCurrentWidth] = useState(300);
  const [currentHeight, setCurrentHeight] = useState(168);

  const statusHistoryFilter = useSelector(GetStatusHistoryFilter);
  const filters = useSelector(GetFilters);
  const filterChannel = useMemo(() => {
    const channels = filters.find((x) => x.id === channelMeta?.id)?.value || [];
    const subChannels = filters.find((x) => x.id === channelChildMeta?.id)?.value || [];
    return [...channels, ...subChannels];
  }, [filters]);
  const filterType = useMemo(() => filters.find((x) => x.id === typeMeta?.id)?.value || [], [filters]);
  const filterProvenance = useMemo(() => filters.find((x) => x.id === provenanceMeta?.id)?.value || [], [filters]);

  const enqueueCustomSnackbar = useCustomSnackbar();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [mouseOffset, setMouseOffset] = useState(1);
  const [spliceThumbnailUrl, setSpliceThumbnailUrl] = useState("");
  const [thumbnailUrl, setThumbnailUrl] = useState("");
  const showHidden = useMemo(() => !filters.find((x) => x.id === hiddenMeta?.id), [filters, filters?.length]);

  const setShowHidden = (show: boolean) => {
    if (!hiddenMeta) return;

    if (show) {
      dispatch(RemoveFilterById(hiddenMeta.id));
      return;
    }

    const metaValue = {
      $type: ComputeMetadataBaseType(BaseMetadataType.DbBoolMetadataValueInfos),
      id: hiddenMeta.id,
      fieldType: BaseMetadataType.DbBoolMetadataValueInfos,
      value: false,
      creationTime: new Date().toISOString(),
      updateTime: null,
      fieldName: null,
      fieldLabel: null,
      targetLayer: ObjectLayer.Asset,
      valueLabel: null,
      hideFilter: true,
    } as IDbBaseMetadataValueInfos;
    dispatch(AddOrUpdateFilter(metaValue));
  };
  const handleRowClick = useCallback(
    (row: GridRowParams<DbAssetObjectInfos>, event: MuiEvent<React.MouseEvent<HTMLElement, MouseEvent>>) => {
      event.preventDefault();
      event.stopPropagation();
      if ((event?.nativeEvent?.target as any)?.nodeName === "IMG") {
        setAnchorEl(event.nativeEvent.target as HTMLElement);
        setSpliceThumbnailUrl(
          thumbnailService.GetMergeThumbsSpliceImageByObjectId(
            row.row.id,
            GlobalConst.BROWSER_IMG_SPRITE_COUNT,
            cacheService.GetAuthKey()!
          )
        );
        handleAR(
          row.row as DbAssetObjectInfos,
          techWidthMeta!.id,
          setCurrentWidth,
          techHeightMeta!.id,
          setCurrentHeight
        );
        setThumbnailUrl(thumbnailService.GetThumbRefByObjectId(row.row.id, cacheService.GetAuthKey()!));
      } else OpenLinkInNewTab(routePaths.asset.path + "/" + row.row.id);
    },
    [techWidthMeta, techHeightMeta, setCurrentWidth, setCurrentHeight]
  );

  // Modals
  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };

  const adamTypeFilter: IDbBaseMetadataValueInfos | null = useMemo(
    () =>
      adamTypeMeta
        ? {
            $type: ComputeMetadataBaseType(BaseMetadataType.DbStringMetadataValueInfos),
            id: adamTypeMeta.id,
            fieldType: BaseMetadataType.DbStringMetadataValueInfos,
            value: "trailer",
            creationTime: new Date().toISOString(),
            updateTime: null,
            fieldName: null,
            fieldLabel: null,
            targetLayer: ObjectLayer.Asset,
            valueLabel: null,
            hideFilter: true,
          }
        : null,
    [adamTypeMeta?.id]
  );

  // Cols
  const columns: GridColDef<DbAssetObjectInfos>[] = useMemo(
    () => [
      {
        field: "thumbnail",
        headerName: "Vignette",
        hideable: true,
        flex: CellSize.small,
        minWidth: CellSize.small,
        sortable: false,
        renderCell: (data) =>
          data.row.status !== ObjectStatus.New && data.row.status !== ObjectStatus.Undefined ? (
            <TableCellAdamVignette url={thumbnailService.GetThumbRefByObjectId(data.row.id, authKey!)} />
          ) : (
            <></>
          ),
      },
      {
        field: "idAsset",
        headerName: t("table:headerName.idAsset"),
        flex: CellSize.small,
        minWidth: CellSize.small,
        isMeta: false,
        valueGetter: (data, row) => row.id,
        renderCell: (data) => (
          <Link
            color="primary.text"
            underline="hover"
            component={RouterLink}
            to={`${routePaths.asset.path}/${data.value}`}
            target="_blank"
          >
            {data.value}
          </Link>
        ),
      },
      {
        field: "object",
        headerName: t("common:infos"),
        flex: CellSize.xxlarge,
        minWidth: CellSize.xxlarge,
        renderCell: (data: any) => <TableCellAdamObjectInfos asset={data.row} />,
      },
      {
        field: "rc_ba_lib_chaine",
        headerName: "Chaine",
        type: "singleSelect",
        valueOptions: CHANNELS_VALUE_LABEL,
        flex: CellSize.xsmall,
        minWidth: CellSize.xsmall,
        isMeta: true,
        valueGetter: (data, row) => GetMetadataByTagName(row, "rc_ba_lib_chaine")?.value,
        renderCell: (data) => (
          <TableCellTypography data={CHANNELS_VALUE_LABEL.find((x) => x.value === data.value)?.miniLabel || ""} />
        ),
      },
      {
        field: "rc_ba_date_diff_ba",
        headerName: t("table:headerName.diffusion"),
        type: "date",
        flex: CellSize.small,
        minWidth: CellSize.small,
        isMeta: true,
        valueGetter: (data, row) => {
          if (!GetMetadataByTagName(row, "rc_ba_date_diff_ba")) return null;
          else return new Date(GetMetadataByTagName(row, "rc_ba_date_diff_ba")!.value);
        },
        renderCell: (data) =>
          data.value ? <TableCellDateTime data={{ value: { date: data.value } }} format={"DD/MM/YYYY HH:mm"} /> : "—",
      },
      {
        field: "rc_ba_dateheure_livraison",
        headerName: "Livraison",
        type: "date",
        flex: CellSize.small,
        minWidth: CellSize.small,
        isMeta: true,
        valueGetter: (data, row) => {
          if (!GetMetadataByTagName(row, "rc_ba_dateheure_livraison")) return null;
          else return new Date(GetMetadataByTagName(row, "rc_ba_dateheure_livraison")!.value);
        },
        renderCell: (data) =>
          data.value ? <TableCellDateTime data={{ value: { date: data.value } }} format={"DD/MM/YYYY HH:mm"} /> : "—",
      },
      {
        field: "rc_ba_dates_diff_emissions",
        headerName: "Diff. PRG",
        type: "string",
        valueGetter: (data, row) => {
          const dateStr = GetMetadataByTagName(row, "rc_ba_dates_diff_emissions")?.value; // this metadata is a string
          return dateStr ? new Date(moment(dateStr, "DD/MM/YYYY HH:mm:ss").format()) : null;
        },
        flex: 150,
        minWidth: 150,
        isMeta: true,
        renderCell: (data) =>
          data.value ? <TableCellDateTime data={{ value: { date: data.value } }} format={"DD/MM/YYYY HH:mm"} /> : "—",
      },
      {
        field: "statusHistory",
        headerName: "Historique statuts",
        flex: CellSize.xlarge,
        minWidth: CellSize.xlarge,
        sortable: false,
        renderCell: (data) => (
          <TableCellAdamStatusHistory
            data={{ ...data.row, status: data?.row.status, history: data?.row.statusHistory }}
          />
        ),
      },
      {
        field: "status",
        headerName: t("common:status"),
        flex: CellSize.medium,
        minWidth: CellSize.medium,
        type: "singleSelect",
        valueOptions: GetEnumValueLabelMap(ObjectStatus, GetObjectStatusLabel).filter(
          (x) => rows.findIndex((o) => o.status === x.value) >= 0
        ),
        isMeta: false,
        renderCell: (data) => (
          <AssetStatus status={data.value} disabled={GetMetadataByTagName(data.row, "isDisabled")?.value} />
        ),
      },
      {
        field: "rc_ba_libelle",
        headerName: "Libelle",
        flex: CellSize.xxlarge,
        minWidth: CellSize.xxlarge,
        isMeta: true,
        valueGetter: (data, row) => GetMetadataByTagName(row, "rc_ba_libelle")?.value,
        renderCell: (data) => <TableCellStringExpend data={data} />,
      },
      {
        field: "rc_ba_titres_emissions",
        headerName: "Titre Prog.",
        flex: CellSize.xlarge,
        minWidth: CellSize.xlarge,
        isMeta: true,
        valueGetter: (data, row) => GetMetadataByTagName(row, "rc_ba_titres_emissions")?.value,
        renderCell: (data) => <TableCellStringExpend data={data} />,
      },
      {
        field: "rc_ba_lib_projet",
        type: "singleSelect",
        valueOptions: LIB_PROJECT_VALUE_LABEL,
        headerName: "Type",
        flex: CellSize.medium,
        minWidth: CellSize.medium,
        isMeta: true,
        valueGetter: (data, row) =>
          GetMetadataByTagName(row, "rc_ba_lib_projet")?.value
            ? LIB_PROJECT_VALUE_LABEL.find((x) => x.value === GetMetadataByTagName(row, "rc_ba_lib_projet")?.value)
                ?.label || ""
            : "",
        renderCell: (data: any) => <TableCellStringExpend data={data} />,
      },
      {
        field: "rc_ba_lib_commande",
        headerName: "Sous-type",
        flex: CellSize.small,
        minWidth: CellSize.small,
        isMeta: true,
        valueGetter: (data, row) => GetMetadataByTagName(row, "rc_ba_lib_commande")?.value,
        renderCell: (data) => <TableCellStringExpend data={data} />,
      },
      {
        field: "rc_ba_lib_sous_classe",
        headerName: "Déclinaison",
        flex: CellSize.small,
        minWidth: CellSize.small,
        isMeta: true,
        valueGetter: (data, row) => GetMetadataByTagName(row, "rc_ba_lib_sous_classe")?.value,
        renderCell: (data) => <TableCellStringExpend data={data} />,
      },
      {
        field: "rc_ba_duree_prevu",
        headerName: "Durée",
        flex: CellSize.small,
        minWidth: CellSize.small,
        isMeta: true,
        valueGetter: (data, row) => GetMetadataByTagName(row, "rc_ba_duree_prevu")?.value,
        renderCell: (data) => <TableCellStringExpend data={data} />,
      },
      {
        field: "durationSMPTE",
        headerName: "Durée (SMPTE)",
        flex: CellSize.small,
        minWidth: CellSize.small,
        isMeta: true,
        valueGetter: (data, row) => GetMetadataByTagName(row, "durationSMPTE")?.value,
        renderCell: (data) => <TableCellStringExpend data={data} />,
      },
      {
        field: "rc_ba_provenance",
        type: "singleSelect",
        valueOptions: PROVENANCE_VALUE_LABEL,
        headerName: "Provenance",
        flex: CellSize.small,
        minWidth: CellSize.small,
        isMeta: true,
        valueGetter: (data, row) =>
          GetMetadataByTagName(row, "rc_ba_provenance")?.value
            ? PROVENANCE_VALUE_LABEL.find((x) => x.value === GetMetadataByTagName(row, "rc_ba_provenance")?.value)
                ?.label
            : "",
        renderCell: (data) => <TableCellStringExpend data={data} />,
      },
      {
        field: "rc_ba_id",
        headerName: "ID BA",
        flex: CellSize.medium,
        minWidth: CellSize.medium,
        isMeta: true,
        valueGetter: (data, row) => GetMetadataByTagName(row, "rc_ba_id")?.value,
        renderCell: (data) => <TableCellStringExpend data={data} copyButton />,
      },
      {
        field: "hab",
        headerName: "HAB",
        type: "date",
        flex: CellSize.xsmall,
        minWidth: CellSize.xsmall,
        isStatusHistory: true,
        status: ObjectStatus.NeedCheckOrEditing,
        valueGetter: (data, row) => {
          if (!row) return;
          const statusHistory = getStatusHisory(row?.statusHistory);
          const spliceFromIndex = getSpliceFromIndex(statusHistory);
          const objectStatus = getObjectStatus(spliceFromIndex, statusHistory);

          return row?.status === ObjectStatus.RejectedAuto
            ? objectStatus.find((x) => x.status === ObjectStatus.RejectedAuto) !== undefined
              ? new Date(objectStatus.find((x) => x.status === ObjectStatus.RejectedAuto).date)
              : null
            : objectStatus.find((x) => x.status === ObjectStatus.NeedCheckOrEditing) !== undefined
            ? new Date(objectStatus.find((x) => x.status === ObjectStatus.NeedCheckOrEditing).date)
            : null;
        },
        renderCell: (data: any) => (
          <CustomStatusRow adamStatus="hab" data={data} validStatus={ObjectStatus.NeedCheckOrEditing} />
        ),
      },
      {
        field: "pav",
        headerName: "PAV",
        type: "date",
        flex: CellSize.xsmall,
        minWidth: CellSize.xsmall,
        isStatusHistory: true,
        status: ObjectStatus.Accepted,
        valueGetter: (data, row) => {
          if (!row) return;
          const statusHistory = getStatusHisory(row?.statusHistory);
          const spliceFromIndex = getSpliceFromIndex(statusHistory);
          const objectStatus = getObjectStatus(spliceFromIndex, statusHistory);

          return row?.status === ObjectStatus.RejectedAuto || row?.status === ObjectStatus.NeedCheckOrEditing
            ? null
            : objectStatus.find((x) => x.status === ObjectStatus.Accepted) !== undefined
            ? new Date(objectStatus.find((x) => x.status === ObjectStatus.Accepted).date)
            : objectStatus.find((x) => x.status === ObjectStatus.Rejected) !== undefined
            ? new Date(objectStatus.find((x) => x.status === ObjectStatus.Rejected).date)
            : null;
        },

        renderCell: (data: any) => (
          <CustomStatusRow
            adamStatus="pav"
            data={data}
            validStatus={ObjectStatus.Accepted}
            errorStatus={ObjectStatus.Rejected}
          />
        ),
      },
      {
        field: "pad",
        headerName: "PAD",
        type: "date",
        flex: CellSize.xsmall,
        minWidth: CellSize.xsmall,
        isStatusHistory: true,
        status: ObjectStatus.AcceptedForBroadcast,
        valueGetter: (data, row) => {
          if (!row) return;
          const statusHistory = getStatusHisory(row?.statusHistory);
          const spliceFromIndex = getSpliceFromIndex(statusHistory);
          const objectStatus = getObjectStatus(spliceFromIndex, statusHistory);

          return row?.status === ObjectStatus.Rejected ||
            row?.status === ObjectStatus.RejectedAuto ||
            row?.status === ObjectStatus.NeedCheckOrEditing
            ? null
            : objectStatus.find((x) => x.status === ObjectStatus.AcceptedForBroadcast) !== undefined
            ? new Date(objectStatus.find((x) => x.status === ObjectStatus.AcceptedForBroadcast).date)
            : objectStatus.find((x) => x.status === ObjectStatus.RejectedForBroadcast) !== undefined
            ? new Date(objectStatus.find((x) => x.status === ObjectStatus.RejectedForBroadcast).date)
            : null;
        },
        renderCell: (data) => (
          <CustomStatusRow
            data={data}
            validStatus={ObjectStatus.AcceptedForBroadcast}
            errorStatus={ObjectStatus.RejectedForBroadcast}
            adamStatus="pad"
          />
        ),
      },
      {
        field: "livr",
        headerName: "LIVR",
        type: "date",
        flex: CellSize.xsmall,
        minWidth: CellSize.xsmall,
        isStatusHistory: true,
        status: ObjectStatus.Delivered,
        valueGetter: (data, row) => {
          if (!row) return;
          const statusHistory = getStatusHisory(row?.statusHistory);
          const spliceFromIndex = getSpliceFromIndex(statusHistory);
          const objectStatus = getObjectStatus(spliceFromIndex, statusHistory);

          return row?.status === ObjectStatus.RejectedAuto ||
            row?.status === ObjectStatus.NeedCheckOrEditing ||
            row?.status === ObjectStatus.Rejected ||
            row?.status === ObjectStatus.RejectedForBroadcast
            ? null
            : objectStatus.find((x) => x.status === ObjectStatus.Delivered) !== undefined
            ? new Date(objectStatus.find((x) => x.status === ObjectStatus.Delivered).date)
            : null;
        },
        renderCell: (data: any) => (
          <CustomStatusRow adamStatus="livr" data={data} validStatus={ObjectStatus.Delivered} />
        ),
      },
      {
        field: "diff",
        headerName: "DIFF",
        flex: CellSize.xsmall,
        minWidth: CellSize.xsmall,
        type: "date",
        isStatusHistory: true,
        status: ObjectStatus.Broadcasted,
        valueGetter: (data, row) => {
          if (!row) return;
          const statusHistory = getStatusHisory(row?.statusHistory);
          const spliceFromIndex = getSpliceFromIndex(statusHistory);
          const objectStatus = getObjectStatus(spliceFromIndex, statusHistory);

          return row?.status === ObjectStatus.Delivered
            ? objectStatus.find((x) => x.status === ObjectStatus.Delivered) !== undefined
              ? new Date(objectStatus.find((x) => x.status === ObjectStatus.Delivered).date)
              : null
            : row?.status === ObjectStatus.RejectedAuto ||
              row?.status === ObjectStatus.NeedCheckOrEditing ||
              row?.status === ObjectStatus.Rejected ||
              row?.status === ObjectStatus.RejectedForBroadcast
            ? null
            : objectStatus.find((x) => x.status === ObjectStatus.Broadcasted) !== undefined
            ? new Date(objectStatus.find((x) => x.status === ObjectStatus.Broadcasted).date)
            : null;
        },
        renderCell: (data) => (
          <CustomStatusRow
            adamStatus="diff"
            data={data}
            validStatus={ObjectStatus.Broadcasted}
            warningStatus={ObjectStatus.Delivered}
          />
        ),
      },
      {
        field: "qcLink",
        headerName: t("common:qcTool"),
        flex: CellSize.xsmall,
        minWidth: CellSize.xsmall,
        sortable: false,
        renderCell: (data) => (
          <Button variant="outlined" onClick={() => handleQcLink(data.row.id)}>
            <TroubleshootIcon sx={{ width: 20, height: 20, color: "text.primary" }} />
          </Button>
        ),
      },
      {
        field: "followers",
        headerName: t("common:followers"),
        flex: CellSize.large,
        minWidth: CellSize.large,
        type: "singleSelect",
        sortable: false,
        valueOptions: (localData) => {
          let result: string[] = [];
          if (!rows) return result;
          rows.forEach((x) => {
            x.followersInfos &&
              x.followersInfos.forEach((f) => {
                if (!followersUsersOptions.find((y) => y.id === f.userId)) return;
                const followerName = followersUsersOptions.find((y) => y.id === f.userId)!.name;
                if (followerName && !result.find((r) => r === followerName)) {
                  result.push(followerName);
                }
              });
          });
          return result;
        },
        valueGetter: (localData, row) => {
          if (!row) return;
          const rowData = rows.find((x) => x.id === row?.id);
          return (
            rowData?.followersInfos?.map(
              (x) => (followersUsersOptions.find((y) => y.id === x.userId) ?? { name: "UNKNOWN" }).name
            ) ?? []
          );
        },
        renderCell: (localData) => {
          const rowData = rows.find((x) => x.id === localData?.id);
          return (
            <Stack sx={{ flexWrap: "wrap", gap: 0.5, my: 1 }} direction={"row"}>
              {rowData && rowData.followersInfos && rowData.followersInfos.length > 0 ? (
                rowData.followersInfos.map((followerInfo: DbObjectCreateOrUpdateInfos) => (
                  <Chip
                    label={
                      (followersUsersOptions.find((x) => x.id === followerInfo.userId) ?? { name: "UNKNOWN" })!.name
                    }
                    size="small"
                    sx={{ mb: 0.5 }}
                  />
                ))
              ) : (
                <Chip label={"-"} size="small" sx={{ mb: 0.5 }} />
              )}
            </Stack>
          );
        },
      },
      {
        field: "rc_ba_droits_debut",
        headerName: "Droit début",
        type: "date",
        flex: CellSize.small,
        minWidth: CellSize.small,
        isMeta: true,
        valueGetter: (data, row) => {
          if (!GetMetadataByTagName(row, "rc_ba_droits_debut")) return null;
          else return new Date(GetMetadataByTagName(row, "rc_ba_droits_debut")!.value);
        },
        renderCell: (data) =>
          data.value ? <TableCellDateTime data={{ value: { date: data.value } }} format={"DD/MM/YYYY HH:mm"} /> : "—",
      },
      {
        field: "rc_ba_droits_fin",
        headerName: "Droit fin",
        type: "date",
        isMeta: true,
        flex: CellSize.small,
        minWidth: CellSize.small,
        valueGetter: (data, row) => {
          if (!GetMetadataByTagName(row, "rc_ba_droits_fin")) return null;
          else return new Date(GetMetadataByTagName(row, "rc_ba_droits_fin")!.value);
        },
        renderCell: (data) =>
          data.value ? <TableCellDateTime data={{ value: { date: data.value } }} format={"DD/MM/YYYY HH:mm"} /> : "—",
      },
      {
        field: "rc_ba_masquer",
        headerName: "Masqué",
        type: "boolean",
        isMeta: true,
        valueGetter: (data, row) => GetMetadataByTagName(row, "rc_ba_masquer")?.value ?? false,
        flex: CellSize.small,
        minWidth: CellSize.small,
      },
      {
        field: "actions",
        headerName: "",
        type: "actions",
        align: "right",
        flex: CellSize.actionsDefault,
        minWidth: CellSize.actionsDefault,
        getActions: (params: GridRowParams) => {
          if (!params || !params.row) return [];
          const isHidden = GetMetadataByTagName(params.row, "rc_ba_masquer")?.value === true;
          let array: any[] = [
            <GridActionsCellItem
              icon={<GroupsOutlinedIcon sx={{ width: 20, height: 20, color: "text.primary" }} />}
              label={t("common:setFollowers")}
              onClick={() => {
                openModal &&
                  openModal<ModalSetFollowersElementProps>(ModalSetFollowers, {
                    objectIds: [params?.row?.id],
                    objectType: "asset",
                    currentFollowersIds:
                      params?.row?.followersInfos?.map((x) => x.userId).filter((id): id is number => id !== null) || [],
                    onConfirm: () => {
                      // todo: refresh followers
                    },
                  });
              }}
              showInMenu
            />,
            <GridActionsCellItem
              icon={
                isHidden ? (
                  <VisibilityIcon sx={{ width: 20, height: 20, color: "text.primary" }} />
                ) : (
                  <VisibilityOffIcon sx={{ width: 20, height: 20, color: "text.primary" }} />
                )
              }
              label={isHidden ? "Afficher" : "Masquer"}
              onClick={() => {
                handleMaskOrShowClick(params?.row?.id, isHidden);
              }}
              showInMenu
            />,
            <GridActionsCellItem
              icon={<VisibilityIcon sx={{ width: 20, height: 20, color: "text.primary" }} />}
              label={t("table:cellLabel.previewAsset")}
              showInMenu
              onClick={() => {
                handlePreviewLink(params?.row?.id);
              }}
            />,
          ];

          let qcTool =
            params.row &&
            (params?.row?.type === ObjectType.Video ||
              params?.row?.type === ObjectType.Audio ||
              params?.row?.type === ObjectType.Subtitle ||
              params?.row?.type === ObjectType.ImageSequence);

          if (qcTool)
            array.push(
              <GridActionsCellItem
                icon={<TroubleshootIcon sx={{ width: 20, height: 20, color: "text.primary" }} />}
                label={t("table:cellLabel.openInQctool")}
                onClick={() => {
                  handleQcLink(params?.row?.id);
                }}
                showInMenu
              />
            );

          let validation =
            (UserHasAcl(AclTypes.aclObjectChangeStatus) && params?.row?.status === ObjectStatus.Accepted) ||
            params?.row?.status === ObjectStatus.NeedCheckOrEditing;

          if (validation) {
            array = [
              ...array,
              ,
              <GridActionsCellItem
                icon={
                  <Tooltip title={t("common:refuser")}>
                    <LockOutlinedIcon sx={{ width: 20, height: 20, color: "text.success" }} />
                  </Tooltip>
                }
                label={t("common:refuser")}
                color="error"
                onClick={() => handleRejectClick(params?.row?.id)}
                showInMenu
              />,
            ];
          }

          return array;
        },
      },
    ],
    [rows]
  );

  const sortInfos = useMemo(() => {
    if (tableSorting && tableSorting.length > 0) {
      const concernedColumn = columns.find((x) => x.field === tableSorting[0].field);
      return {
        type: (concernedColumn as any).isMeta
          ? SortingType.Metadata
          : (concernedColumn as any).isStatusHistory
          ? SortingType.StatusHistory
          : SortingType.Property,
        name: (concernedColumn as any).isStatusHistory ? (concernedColumn as any).status : tableSorting[0].field,
        desc: tableSorting[0].sort === "desc",
      };
    } else if (
      ["rc_ba_dateheure_livraison", "rc_ba_date_diff_ba", "rc_ba_droits_debut", "rc_ba_droits_fin"].includes(
        filterDateType
      )
    ) {
      return {
        type: SortingType.Metadata,
        name: filterDateType,
        desc: false,
      };
    } else if (
      ["NeedCheckOrEditing", "Accepted", "AcceptedForBroadcast", "Delivered", "Broadcasted"].includes(filterDateType)
    ) {
      return {
        type: SortingType.StatusHistory,
        name: filterDateType,
        desc: false,
      };
    }
  }, [tableSorting, filterDateType]);

  // handle page cached state
  useEffect(() => {
    const pageCache:
      | {
          condensed: boolean;
          filterToDeliver: boolean;
          filterAssignedToMe: boolean;
          showHidden: boolean;
          searchExpanded: boolean;
          filters: IDbBaseMetadataValueInfos[] | undefined;
          linkedDateFilterId: number | null;
          linkedStatusHistoryFilter: ObjectStatus | null;
          statusHistoryFilter: StatusHistoryFilter[] | undefined;
          searchPattern: string | undefined;
        }
      | undefined
      | null = cacheService.GetPageCache(page);
    if (pageCache) {
      setFilterAssignedToMe(pageCache.filterAssignedToMe);
      setFilterToDeliver(pageCache.filterToDeliver);
      setShowHidden(pageCache.showHidden);
      setSearchExpanded(pageCache.searchExpanded);
      setLinkedDateFilterId(pageCache.linkedDateFilterId);
      setLinkedStatusHistoryFilter(pageCache.linkedStatusHistoryFilter);
      if (pageCache.statusHistoryFilter && pageCache.statusHistoryFilter.length > 0) {
        dispatch(AddOrUpdateStatusHistoryFilter(pageCache.statusHistoryFilter[0]));
        const filterType = pageCache.statusHistoryFilter[0].status;
        setFilterDateType(filterType);
        setDateValue([
          dayjs(pageCache.statusHistoryFilter[0].startDate),
          dayjs(pageCache.statusHistoryFilter[0].endDate),
        ]);
      }
      if (pageCache.filters && pageCache.filters.length > 0) {
        dispatch(StoreFiltersState(pageCache.filters));
        let dateRange;
        if (pageCache.filters.find((x) => x.id === dateLivraison?.id)) {
          setFilterDateType("rc_ba_dateheure_livraison");
          const startDate = pageCache.filters.find(
            (x) => x.id === dateLivraison?.id && x.valueLabel === "startDate"
          )?.value;
          const endDate = pageCache.filters.find(
            (x) => x.id === dateLivraison?.id && x.valueLabel === "endDate"
          )?.value;
          dateRange = [startDate, endDate];
        }
        if (pageCache.filters.find((x) => x.id === dateDiff?.id)) {
          setFilterDateType("rc_ba_date_diff_ba");
          const startDate = pageCache.filters.find((x) => x.id === dateDiff?.id && x.valueLabel === "startDate")?.value;
          const endDate = pageCache.filters.find((x) => x.id === dateDiff?.id && x.valueLabel === "endDate")?.value;
          dateRange = [startDate, endDate];
        }
        if (pageCache.filters.find((x) => x.id === dateRightsStart?.id)) {
          setFilterDateType("rc_ba_droits_debut");
          const startDate = pageCache.filters.find(
            (x) => x.id === dateRightsStart?.id && x.valueLabel === "startDate"
          )?.value;
          const endDate = pageCache.filters.find(
            (x) => x.id === dateRightsStart?.id && x.valueLabel === "endDate"
          )?.value;
          dateRange = [startDate, endDate];
        }
        if (pageCache.filters.find((x) => x.id === dateRightsEnd?.id)) {
          setFilterDateType("rc_ba_droits_fin");
          const startDate = pageCache.filters.find(
            (x) => x.id === dateRightsEnd?.id && x.valueLabel === "startDate"
          )?.value;
          const endDate = pageCache.filters.find(
            (x) => x.id === dateRightsEnd?.id && x.valueLabel === "endDate"
          )?.value;
          dateRange = [startDate, endDate];
        }
        if (pageCache.searchPattern) setSearchPattern(pageCache.searchPattern);
        if (dateRange && dateRange[0] && dateRange[1]) setDateValue([dayjs(dateRange[0]), dayjs(dateRange[1])]);
      }
    }
    if (!pageCache || !(pageCache.filters && pageCache.filters.length > 0)) {
      handleAddDateFilter(
        dateValue,
        filterDateType,
        dispatch,
        setLinkedDateFilterId,
        today,
        undefined,
        dateLivraison,
        dateDiff,
        dateRightsStart,
        dateRightsEnd,
        setLinkedStatusHistoryFilter
      );
    }

    return () => {
      dispatch(RemoveAllFilters());
    };
  }, [page]);

  useEffect(() => {
    if (filters.length > 0) search();
  }, [sortInfos?.desc, sortInfos?.name, sortInfos?.type, filters.length === 0]);

  useEffect(() => {
    adamTypeFilter && dispatch(AddOrUpdateFilter(adamTypeFilter));
  }, [adamTypeFilter]);

  useEffect(() => {
    cacheService.SetPageCache(page, {
      filterToDeliver,
      filterAssignedToMe,
      showHidden,
      searchExpanded,
      filters: filters,
      linkedDateFilterId: linkedDateFilterId,
      linkedStatusHistoryFilter: linkedStatusHistoryFilter,
      statusHistoryFilter: statusHistoryFilter,
      searchPattern: searchPattern,
    });
    setSearchChange(true);
  }, [
    filterToDeliver,
    filterAssignedToMe,
    showHidden,
    searchExpanded,
    filters,
    filters.length,
    statusHistoryFilter,
    linkedStatusHistoryFilter,
    linkedDateFilterId,
    searchPattern,
  ]);

  const {
    selectedItems: selectedItems,
    setSelectedItems,
    reload,
  } = useOutletContext<{
    setSelectedItems: React.Dispatch<React.SetStateAction<DbAssetObjectInfos[]>>;
    selectedItems: DbAssetObjectInfos[];
    reload: number;
  }>();

  useEffect(() => {
    userService.GetModalSetFollowersData().then((data) => {
      const _followersUsersOptions = data
        .flatMap((x) => x.followers)
        .sort((a, b) => {
          const nameA = a.name.toLowerCase();
          const nameB = b.name.toLowerCase();
          return nameA < nameB ? -1 : nameA > nameB ? 1 : a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
        });

      setFollowersUsersOptions(_followersUsersOptions);
    });
  }, []);

  const authKey = cacheService.GetAuthKey();

  // Handlers
  const handleMaskOrShowClick = (id: number, isHidden: boolean) => {
    if (!openModal || !hiddenMeta) return;
    openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
      displayComment: false,
      title: isHidden ? "Montrer l'Asset ?" : "Masquer l'Asset ?",
      onConfirm: () => {
        assetService
          .UpdateMetadataFields(
            new UpdateMetadataFieldsRequest({
              metadata: [
                CreateNewDbMetadataValue(
                  hiddenMeta!.id,
                  BaseMetadataType.DbBoolMetadataValueInfos,
                  hiddenMeta.tagName!,
                  !isHidden
                ),
              ],
              objectId: id,
            })
          )
          .then((response) => {
            if (response.success) {
              enqueueCustomSnackbar(t("alert:success.metadataUpdated"), "success");
              const currentRow = rows.find((x) => x.id === id);
              if (!currentRow) return;
              const newRow = {
                ...currentRow,
                metadata: currentRow.metadata
                  ? currentRow.metadata?.map((m) => (m.id === hiddenMeta.id ? { ...m, value: !isHidden } : m))
                  : currentRow.metadata,
              };
              setRows((x) =>
                x.map((r) => {
                  if (r.id === id) {
                    return newRow;
                  } else return r;
                })
              );
              apiGrid.current.updateRows([newRow]);
            } else enqueueCustomSnackbar(t("alert:error.updatingMetadata"), "error");
          });
      },
    });
  };

  const handleRejectClick = (id: number) => {
    if (!openModal) return;

    let asset = rows.find((x) => x.id === id);

    if (asset!.status === ObjectStatus.NeedCheckOrEditing) {
      openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
        displayComment: true,
        commentLabel: t("modal:body.letUsMessageToExplainYourSeason"),
        title: t("modal:title.rejectQuestionMark"),
        onConfirm: (comment) => {
          let request = new ChangeStatusRequest({
            status: ObjectStatus.Rejected,
            message: comment,
            objectId: id,
          });
          assetService.ChangeStatus(request).then((response) => {
            if (response.success) enqueueCustomSnackbar(t("alert:success.statusChangedAndObjectRejected"), "success");
          });
        },
      });
    } else if (asset!.status === ObjectStatus.Accepted) {
      openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
        displayComment: false,
        title: t("modal:title.rejectQuestionMark"),
        onConfirm: () => {
          let request = new ChangeStatusRequest({
            status: ObjectStatus.RejectedForBroadcast,
            message: "Rejected",
            objectId: id,
          });
          assetService.ChangeStatus(request).then((response) => {
            if (response.success)
              enqueueCustomSnackbar(t("alert:success.statusChangedAndObjectRejectedForBroadcast"), "success");
          });
        },
      });
    }
  };

  const handleSelectionModelChange = useCallback(
    (newSelectedRows: GridRowSelectionModel) => {
      const selectedIds = newSelectedRows as number[];
      setSelectedItems(rows.filter((x) => selectedIds.includes(x.id)));
    },
    [rows]
  );

  const handleQcLink = (id: number) => {
    window.open(routePaths.toolsQc.path + "/" + id, "_blank");
  };

  const handlePreviewLink = (id: number) => {
    window.open(routePaths.asset.path + "/" + id, "_blank");
  };

  const handleFilterProvenance = useCallback(
    (event: SelectChangeEvent<typeof filterProvenance>) => {
      if (!provenanceMeta) return;
      const {
        target: { value },
      } = event;
      const newValue = typeof value === "string" ? value.split(",") : value;
      if (newValue.length === 0) {
        dispatch(RemoveFilterById(provenanceMeta.id));
        return;
      }
      const metaValue = {
        $type: ComputeMetadataBaseType(BaseMetadataType.DbStringMultiMetadataValueInfos),
        id: provenanceMeta.id,
        fieldType: BaseMetadataType.DbStringMultiMetadataValueInfos,
        value: newValue,
        creationTime: new Date().toISOString(),
        updateTime: null,
        fieldName: null,
        fieldLabel: null,
        targetLayer: ObjectLayer.Asset,
        valueLabel: null,
      };
      dispatch(AddOrUpdateFilter(metaValue));
      setSearchChange(true);
    },
    [provenanceMeta]
  );

  const handleFilterType = useCallback(
    (event: SelectChangeEvent<typeof filterType>) => {
      if (!typeMeta) return;
      const {
        target: { value },
      } = event;
      const newValue = typeof value === "string" ? value.split(",") : value;
      if (newValue.length === 0) {
        dispatch(RemoveFilterById(typeMeta.id));
        return;
      }
      const metaValue = {
        $type: ComputeMetadataBaseType(BaseMetadataType.DbStringMultiMetadataValueInfos),
        id: typeMeta.id,
        fieldType: BaseMetadataType.DbStringMultiMetadataValueInfos,
        value: newValue,
        creationTime: new Date().toISOString(),
        updateTime: null,
        fieldName: null,
        fieldLabel: null,
        targetLayer: ObjectLayer.Asset,
        valueLabel: null,
      };
      dispatch(AddOrUpdateFilter(metaValue));
      setSearchChange(true);
    },
    [typeMeta]
  );

  const handleFilterHiddenChange = useCallback(
    (event) => {
      if (!hiddenMeta) return;
      setShowHidden(event.target.checked);
      if (event.target.checked) {
        dispatch(RemoveFilterById(hiddenMeta.id));
        return;
      }

      const metaValue = {
        $type: ComputeMetadataBaseType(BaseMetadataType.DbBoolMetadataValueInfos),
        id: hiddenMeta.id,
        fieldType: BaseMetadataType.DbBoolMetadataValueInfos,
        value: false,
        creationTime: new Date().toISOString(),
        updateTime: null,
        fieldName: null,
        fieldLabel: null,
        targetLayer: ObjectLayer.Asset,
        valueLabel: null,
        hideFilter: true,
      } as IDbBaseMetadataValueInfos;
      dispatch(AddOrUpdateFilter(metaValue));
      setSearchChange(true);
    },
    [hiddenMeta]
  );

  const handleFilterToDeliverChange = useCallback(
    (event) => {
      setFilterToDeliver(event.target.checked);
      cacheService.SetPageCache(page, {
        filterToDeliver: event.target.checked,
        filterAssignedToMe,
        showHidden,
        searchExpanded,
        filters: filters,
        linkedDateFilterId,
        linkedStatusHistoryFilter,
        statusHistoryFilter,
        searchPattern,
      });
      setSearchChange(true);
    },
    [
      filterToDeliver,
      filterAssignedToMe,
      showHidden,
      searchExpanded,
      filters,
      linkedDateFilterId,
      linkedStatusHistoryFilter,
      statusHistoryFilter,
      searchPattern,
    ]
  );

  const handleFilterAssignedToMe = useCallback(
    (event) => {
      setFilterAssignedToMe(event.target.checked);
      cacheService.SetPageCache(page, {
        filterToDeliver,
        filterAssignedToMe: event.target.checked,
        showHidden,
        searchExpanded,
        filters: filters,
        linkedDateFilterId,
        linkedStatusHistoryFilter,
        statusHistoryFilter,
        searchPattern,
      });

      setSearchChange(true);
    },
    [
      filterToDeliver,
      filterAssignedToMe,
      showHidden,
      searchExpanded,
      filters,
      linkedDateFilterId,
      linkedStatusHistoryFilter,
      statusHistoryFilter,
      searchPattern,
    ]
  );

  const handleFilterDateType = useCallback(
    (event) => {
      linkedDateFilterId && dispatch(RemoveFilterById(linkedDateFilterId));
      linkedStatusHistoryFilter && dispatch(RemoveStatusHistoryFilter(linkedStatusHistoryFilter));
      handleAddDateFilter(
        dateValue,
        event.target.value,
        dispatch,
        setLinkedDateFilterId,
        today,
        undefined,
        dateLivraison,
        dateDiff,
        dateRightsStart,
        dateRightsEnd,
        setLinkedStatusHistoryFilter
      );
      setFilterDateType(event.target.value);
    },
    [
      linkedDateFilterId,
      linkedStatusHistoryFilter,
      dateValue,
      dispatch,
      setLinkedDateFilterId,
      today,
      undefined,
      dateLivraison,
      dateDiff,
      dateRightsStart,
      dateRightsEnd,
      setLinkedStatusHistoryFilter,
    ]
  );

  const handleDateChange = useCallback(
    (newValue) => {
      linkedDateFilterId && dispatch(RemoveFilterById(linkedDateFilterId));
      linkedStatusHistoryFilter && dispatch(RemoveStatusHistoryFilter(linkedStatusHistoryFilter));
      handleAddDateFilter(
        newValue,
        filterDateType,
        dispatch,
        setLinkedDateFilterId,
        today,
        undefined,
        dateLivraison,
        dateDiff,
        dateRightsStart,
        dateRightsEnd,
        setLinkedStatusHistoryFilter
      );
      setDateValue(newValue);
    },
    [
      linkedDateFilterId,
      linkedStatusHistoryFilter,
      filterDateType,
      dispatch,
      setLinkedDateFilterId,
      today,
      undefined,
      dateLivraison,
      dateDiff,
      dateRightsStart,
      dateRightsEnd,
      setLinkedStatusHistoryFilter,
    ]
  );

  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 20,
  });

  const search = (addToResult: boolean = false, pagination?: { page: number; pageSize: number }) => {
    setIsLoading(true);
    setSearchChange(false);
    setNoResult(false);
    setError(false);
    if (!addToResult) {
      setRows([]);
      dispatch(CacheResults([]));
      if (apiGrid.current?.setPage && paginationModel.page !== 0) {
        setPaginationModel((x) => ({ page: 0, pageSize: x.pageSize }));
        apiGrid.current.setPage(0);
        return;
      }
    } else {
      setIsFetchingNewPage(true);
    }

    const userId = cacheService.GetUserInfo()?.id;
    const p = pagination ? pagination.page : paginationModel.page;
    const pageToQuery = addToResult ? p : 0;
    searchService
      .SearchBasic(
        new SearchRequestNative({
          outputLayer: ObjectLayer.Asset,
          hideTitleWithoutAsset: false,
          pattern: searchPattern,
          metadata: filters?.filter((x) => x.value !== null && x.value !== undefined),
          classifications: [ObjectClass.Drive],
          types: [ObjectType.Video, ObjectType.Image],
          statuses: filterToDeliver
            ? [
                ObjectStatus.Undefined,
                ObjectStatus.Delivered,
                ObjectStatus.New,
                ObjectStatus.ParsedAndProbbed,
                ObjectStatus.ToBeQCed,
                ObjectStatus.Rejected,
                ObjectStatus.RejectedAuto,
                ObjectStatus.Accepted,
                ObjectStatus.AcceptedForBroadcast,
                ObjectStatus.RejectedForBroadcast,
                ObjectStatus.NeedCheckOrEditing,
              ]
            : null,
          statusHistory: statusHistoryFilter,
          onlyConfidential: false,
          titleCustomerIds: null,
          assetIds: null,
          createdBy: null,
          providerIds: null,
          limit: pagination ? pagination.pageSize : paginationModel.pageSize,
          page: pageToQuery,
          sort: sortInfos,
          skipDeleted: true,
          followerIds: filterAssignedToMe && userId ? [userId] : null,
        })
      )
      .then(function (rep) {
        if (rep.results && rep.results.length > 0) {
          setCount(rep.count ?? 0);
          setRows([...rep.results.map((x) => ({ ...x, statusHistory: [...(x.statusHistory ?? [])].reverse() }))]);
          setNoResult(false);

          cacheService.SetPageCache(page, {
            filterToDeliver,
            filterAssignedToMe,
            showHidden,
            searchExpanded,
            filters: filters,
            linkedDateFilterId: linkedDateFilterId,
            linkedStatusHistoryFilter: linkedStatusHistoryFilter,
            statusHistoryFilter: statusHistoryFilter,
            searchPattern: searchPattern,
          });
        } else {
          setNoResult(true);
          setCount(0);
        }
        setTimeout(() => {
          setIsFetchingNewPage(false);
        }, 500);

        setIsLoading(false);
      })
      .catch((err) => {
        onError && onError();
        setIsLoading(false);
        setIsFetchingNewPage(false);
      });
  };

  const onError = () => {
    setError(true);
    setIsLoading(false);
  };

  const setFilterChannel = useCallback(
    (value) => {
      if (!channelMeta || !channelChildMeta) return;

      const chaineValues = value.filter((x) => !x.includes("OKOO"));
      const subChaineValues = value.filter((x) => x.includes("OKOO"));
      if (chaineValues.length > 0) {
        const metaValue = {
          $type: ComputeMetadataBaseType(BaseMetadataType.DbStringMultiMetadataValueInfos),
          id: channelMeta.id,
          fieldType: BaseMetadataType.DbStringMultiMetadataValueInfos,
          value: chaineValues,
          creationTime: new Date().toISOString(),
          updateTime: null,
          fieldName: null,
          fieldLabel: null,
          targetLayer: ObjectLayer.Asset,
          valueLabel: null,
        };
        dispatch(AddOrUpdateFilter(metaValue));
      } else {
        dispatch(RemoveFilterById(channelMeta.id));
      }
      if (subChaineValues.length > 0) {
        const metaValueSub = {
          $type: ComputeMetadataBaseType(BaseMetadataType.DbStringMultiMetadataValueInfos),
          id: channelChildMeta.id,
          fieldType: BaseMetadataType.DbStringMultiMetadataValueInfos,
          value: subChaineValues,
          creationTime: new Date().toISOString(),
          updateTime: null,
          fieldName: null,
          fieldLabel: null,
          targetLayer: ObjectLayer.Asset,
          valueLabel: null,
        };
        dispatch(AddOrUpdateFilter(metaValueSub));
      } else {
        dispatch(RemoveFilterById(channelChildMeta.id));
      }
    },
    [channelMeta, channelChildMeta]
  );

  const openMenu = Boolean(anchorEl);

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleSortModelChange = (newModelSorting: GridSortModel) => {
    cacheService.SetTableSort(TableUuids.ADAM_TRAILERS, newModelSorting);
    setTableSorting(newModelSorting ?? []);
  };

  const _table = useMemo(
    () => (
      <DataTable
        tableKey={TableUuids.ADAM_TRAILERS}
        sortModel={tableSorting}
        onSortModelChange={(val) => {
          handleSortModelChange(val);
        }}
        paginationMode="server"
        paginationServerSideChangeCallback={(page, pageSize) => {
          setPaginationModel({ page, pageSize });
          filters.length > 0 && search(true, { page, pageSize });
        }}
        apiRef={apiGrid}
        columnHeaderHeight={24}
        initialColumnVisibility={{
          idAsset: true,
          thumbnail: true,
          object: true,
          rc_ba_lib_chaine: true,
          rc_ba_date_diff_ba: true,
          rc_ba_dates_diff_emissions: false,
          rc_ba_dateheure_livraison: false,
          statusHistory: true,
          status: false,
          rc_ba_libelle: false,
          rc_ba_lib_projet: false,
          rc_ba_lib_commande: false,
          rc_ba_lib_sous_classe: false,
          rc_ba_duree_prevu: false,
          durationSMPTE: false,
          rc_ba_provenance: false,
          rc_ba_id: false,
          rc_ba_titres_emissions: false,
          hab: false,
          pav: false,
          pad: false,
          livr: false,
          diff: false,
          qcLink: false,
          rc_ba_droits_fin: false,
          rc_ba_droits_debut: false,
          followers: false,
          rc_ba_masquer: false,
          actions: true,
        }}
        columns={columns}
        rows={rows}
        checkboxSelection
        disableRowSelectionOnClick
        onRowClick={handleRowClick}
        selectedRows={selectedItems.map((x) => x.id)}
        onRowSelectionModelChange={handleSelectionModelChange}
        loading={isLoading || isFetchingNewPage}
        densityControl
        rowCount={count}
      />
    ),
    [columns, count, isLoading, isFetchingNewPage, rows, selectedItems]
  );

  useAdamDateFilterLinkValues(
    dateValue,
    setDateValue,
    linkedDateFilterId,
    linkedStatusHistoryFilter,
    filters,
    statusHistoryFilter
  );

  return (
    <>
      <Stack direction={"row"} display={"flex"} justifyContent={"space-between"} width={"100%"}>
        <FiltersList
          sx={{ mb: 2, pt: 2 }}
          layer={ObjectLayer.Asset}
          type={MetadataFieldValueObjectTypes.DbAssetObjectInfos}
        />
        <Stack alignItems={"center"} direction={"row"}>
          <FormControlLabel
            checked={showHidden}
            onChange={handleFilterHiddenChange}
            control={<Checkbox color="info" />}
            label={t("field:label.showHidden")}
          />
          <FormControlLabel
            checked={filterToDeliver}
            onChange={handleFilterToDeliverChange}
            control={<Checkbox color="info" />}
            label={t("field:label.toDeliver")}
          />
          <FormControlLabel
            checked={filterAssignedToMe}
            onChange={handleFilterAssignedToMe}
            control={<Checkbox color="info" />}
            label={t("field:label.myAssignations")}
            sx={{ p: 0, m: 0 }}
          />
        </Stack>
      </Stack>

      <AdamChannelsPageFilters
        setFilterChannel={setFilterChannel}
        filterChannel={filterChannel}
        rows={rows}
        isSearch={true}
      />

      <Stack height={"56px"} direction={"row"} justifyContent={"space-between"} pt={"8px"}>
        <Stack flexWrap="nowrap" direction="row" spacing={1} alignItems={"flex-start"}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Select value={filterDateType} startAdornment={<CalendarMonthIcon />} onChange={handleFilterDateType}>
              <MenuItem value={"rc_ba_dateheure_livraison"}>{t("field:placeholder.deliveryDate")}</MenuItem>
              <MenuItem value={"rc_ba_date_diff_ba"}>{t("field:placeholder.broadcastingDate")}</MenuItem>
              <MenuItem value={"rc_ba_droits_debut"}>{t("field:placeholder.rightStartDate")}</MenuItem>
              <MenuItem value={"rc_ba_droits_fin"}>{t("field:placeholder.rightEndDate")}</MenuItem>
              <MenuItem value={"NeedCheckOrEditing"}>Date HAB</MenuItem>
              <MenuItem value={"Accepted"}>Date PAV</MenuItem>
              <MenuItem value={"AcceptedForBroadcast"}>Date PAD</MenuItem>
              <MenuItem value={"Delivered"}>Date LIVR</MenuItem>
              <MenuItem value={"Broadcasted"}>Date DIFF</MenuItem>
            </Select>

            <DateRangePicker
              slotProps={{
                shortcuts: {
                  items: shortcutsItems,
                  subheader: (
                    <Button
                      onClick={() => {
                        handleDateChange([null, dateValue[1]]);
                      }}
                      fullWidth
                      endIcon={<CloseOutlined />}
                    >
                      Clear start date
                    </Button>
                  ),
                },
              }}
              disableHighlightToday
              disableAutoMonthSwitching={true}
              closeOnSelect={false}
              displayWeekNumber={true}
              fixedWeekNumber={5}
              showDaysOutsideCurrentMonth={true}
              disabled={!filterDateType}
              slots={{ field: SingleInputDateRangeField }}
              sx={{
                position: "relative",
                bottom: "24px",
              }}
              value={dateValue}
              onChange={handleDateChange}
              format="DD/MM/YYYY"
            />
          </LocalizationProvider>
          <Button
            onClick={() => {
              setDateValue([null, null]);
              dispatch(RemoveFilterById(linkedDateFilterId));
              linkedStatusHistoryFilter && dispatch(RemoveStatusHistoryFilter(linkedStatusHistoryFilter));
            }}
            variant="outlined"
            color="primary"
            disabled={!dateValue[0] && !dateValue[1]}
          >
            {t("common:reset")}
          </Button>
        </Stack>
        <Stack flexWrap="nowrap" direction="row" spacing={1} alignItems={"flex-start"}>
          <Select
            sx={{ maxWidth: "180px" }}
            displayEmpty
            IconComponent={ExpandMoreRoundedIcon}
            value={filterProvenance}
            inputProps={{ "aria-label": "Without label" }}
            MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
            startAdornment={<PlaceIcon />}
            onChange={handleFilterProvenance}
            renderValue={(v) => {
              if (v.length === 0) return "Provenance";
              else return v.map((value) => value.toString()).join(", ");
            }}
            multiple
          >
            {PROVENANCE_VALUE_LABEL.map((x, i) => (
              <MenuItem value={x.value} key={i}>
                {x.label}
              </MenuItem>
            ))}
          </Select>
          <Select
            sx={{ maxWidth: "180px" }}
            displayEmpty
            IconComponent={ExpandMoreRoundedIcon}
            value={filterType}
            inputProps={{ "aria-label": "Without label" }}
            MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
            startAdornment={<ClassIcon />}
            onChange={handleFilterType}
            renderValue={(v) => {
              if (v.length === 0) return "Type";
              else return v.map((value) => value.toString()).join(", ");
            }}
            multiple
          >
            {LIB_PROJECT_VALUE_LABEL.map((x, i) => (
              <MenuItem value={x.value} key={i}>
                {x.label}
              </MenuItem>
            ))}
          </Select>
          <OutlinedInput
            sx={{ width: "250px" }}
            label={t("common:search")}
            placeholder={t("field:placeholder.searchByExpression")}
            value={searchPattern}
            onChange={(value) => setSearchPattern(value.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                search();
              }
            }}
            endAdornment={
              <InputAdornment position="end">
                {searchPattern && (
                  <IconButton aria-label="Clear field" onClick={() => setSearchPattern("")} size="small" sx={{ mr: 1 }}>
                    <CloseRoundedIcon />
                  </IconButton>
                )}
                <SearchIcon />
              </InputAdornment>
            }
          />
          <Button
            onClick={() => {
              search();
            }}
            variant="contained"
            color="primary"
            startIcon={<SearchIcon />}
            disabled={isLoading && !searchChange}
          >
            {t("common:search")}
          </Button>
        </Stack>
      </Stack>

      {noResult && (
        <EmptyListSlot
          title={t("alert:info.noSearchResult")}
          desc={t("component:listPageHeaderLeft.title.resultsForPattern_pattern_zero", {
            context: "pattern_zero",
            pattern: searchPattern,
          })}
          icon={<CollectionsBookmarkOutlinedIcon />}
          sx={{ background: "transparent" }}
          height="100%"
          closeButton={() => setNoResult(false)}
        ></EmptyListSlot>
      )}
      {!noResult && error && (
        <Box
          sx={(theme) => ({
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            width: "100%",
            mt: 3,
            border: `1px solid ${theme.palette.background.paper}`,
            borderRadius: 1,
            flexDirection: "column",
          })}
        >
          <ErrorIcon icon={<ErrorOutline color="disabled" />} />
          <Typography color="text.tier" mt={1.5} variant="body2">
            {t("alert:error.anErrorOccurred")}
          </Typography>
        </Box>
      )}

      {_table}

      <Menu
        id="long-menu"
        slotProps={{
          paper: {
            style: {
              maxHeight: `${GlobalConst.BROWSER_IMG_SPRITE_HEIGHT_PX}px`,
              width: `${GlobalConst.BROWSER_IMG_SPRITE_HEIGHT_PX * (currentWidth / currentHeight)}px`,
              overflow: "hidden",
            },
          },
          list: {
            style: { display: "flex", justifyContent: "center" },
          },
        }}
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleClose}
      >
        <AdamThumbnailBody
          spliceThumbnailUrl={spliceThumbnailUrl}
          thumbnailUrl={thumbnailUrl}
          onMouseOver={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          mouseOffset={mouseOffset - 1}
          techW={currentWidth}
          techH={currentHeight}
        >
          <figure
            onMouseLeave={(e) => setMouseOffset(1)}
            onMouseMove={(e) => {
              if (!(spliceThumbnailUrl && spliceThumbnailUrl !== "")) return;
              let posInDiv = e.clientX - e.currentTarget.getBoundingClientRect().left;
              let divWidth = e.currentTarget.getBoundingClientRect().width;
              if (divWidth === 0) return;

              let blockWidth = divWidth / GlobalConst.BROWSER_IMG_SPRITE_COUNT; // Size of each block in px
              let percent = posInDiv / divWidth; // Position in the div in %
              let blockIndex = Math.trunc((percent * divWidth) / blockWidth); // Position in the div in blocks

              let offset = blockIndex * GlobalConst.BROWSER_IMG_SPRITE_HEIGHT_PX;
              setMouseOffset(-offset + (blockIndex + 1) / 2);
              const cursorVT = document.getElementById("vt");
              cursorVT &&
                cursorVT.setAttribute(
                  "style",
                  `left: ${posInDiv}px; position: absolute; top: 0; bottom: 0; width: 1px; background: ${theme.palette.primary.main}; z-index: 2600;`
                );
            }}
          >
            <Box
              sx={{
                width: `${GlobalConst.BROWSER_IMG_SPRITE_HEIGHT_PX * (currentWidth / currentHeight)}px`,
                height: `${GlobalConst.BROWSER_IMG_SPRITE_HEIGHT_PX}px`,
                position: "relative",
              }}
            >
              <div id="vt"></div>
            </Box>
          </figure>
        </AdamThumbnailBody>
      </Menu>
    </>
  );
};

export default AdamTrailers;

export const AdamThumbnailBody = styled("div", {
  shouldForwardProp: (propName) =>
    propName !== "mouseOffset" &&
    propName !== "thumbnailUrl" &&
    propName !== "spliceThumbnailUrl" &&
    propName !== "techW" &&
    propName !== "techH",
})<{
  mouseOffset: number; // css image sprite
  spliceThumbnailUrl: string;
  thumbnailUrl: string;
  techW: number;
  techH: number;
}>(({ theme, mouseOffset, thumbnailUrl, spliceThumbnailUrl, techH, techW }) => ({
  width: `${GlobalConst.BROWSER_IMG_SPRITE_HEIGHT_PX * (techW / techH)}px`,
  height: `${GlobalConst.BROWSER_IMG_SPRITE_HEIGHT_PX}px`,
  background: theme.palette.background.paper,
  figure: {
    position: "relative",
    width: "100%",
    height: "100%",
    paddingBottom: `${(192 / 143) * 65}%`,
    borderRadius: 3,
    overflow: "hidden",
    background:
      mouseOffset !== 1 ? "url('" + spliceThumbnailUrl + "') 0px " + mouseOffset + "px" : "url('" + thumbnailUrl + "')",
    backgroundSize: mouseOffset !== 1 ? "cover" : "contain",
    backgroundPosition: "auto",
    backgroundRepeat: "no-repeat",
  },
  img: {
    position: "absolute",
    height: "100%",
    width: "100%",
  },
}));

const CustomStatusRow: FC<{
  data: any;
  validStatus: ObjectStatus;
  errorStatus?: ObjectStatus;
  warningStatus?: ObjectStatus;
  adamStatus: "hab" | "pav" | "pad" | "livr" | "diff";
}> = ({ data, validStatus, errorStatus, warningStatus, adamStatus }) => {
  const theme = useTheme();
  const currentStatus = data.row?.status;
  const statusHistory = getStatusHisory(data.row?.statusHistory);
  const spliceFromIndex = getSpliceFromIndex(statusHistory);
  const objectStatus = getObjectStatus(spliceFromIndex, statusHistory);
  const formatValue: string[] | undefined = data?.value
    ? moment(data.value).format("DD/MM/YYYY HH:mm").split(" ")
    : undefined;

  const formatValueNoYear: string[] | undefined = data?.value
    ? moment(data.value).format("DD/MM HH:mm").split(" ")
    : undefined;

  const getFinalColor = () => {
    if (adamStatus === "hab" && currentStatus === ObjectStatus.RejectedAuto) return "#f80000";
    if (adamStatus === "livr" && currentStatus === ObjectStatus.Delivered) return "#39983f";

    if (
      (ObjectStatus.NeedCheckOrEditing === currentStatus || ObjectStatus.RejectedAuto === currentStatus) &&
      adamStatus !== "hab"
    )
      return theme.palette.grey[500];
    if (
      currentStatus === ObjectStatus.Rejected &&
      (adamStatus === "pad" || adamStatus === "livr" || adamStatus === "diff")
    )
      return theme.palette.grey[500];
    if (currentStatus === ObjectStatus.RejectedForBroadcast && (adamStatus === "livr" || adamStatus === "diff"))
      return theme.palette.grey[500];

    return currentStatus === warningStatus
      ? "#fba31d"
      : objectStatus.find((x) => x.status === validStatus) !== undefined
      ? "#39983f"
      : objectStatus.find((x) => errorStatus && x.status === errorStatus) !== undefined
      ? "#f80000"
      : theme.palette.grey[500];
  };

  const getFinalTootip = () => {
    if (adamStatus === "hab" && currentStatus === ObjectStatus.RejectedAuto)
      return objectStatus.find((x) => errorStatus && x.status === errorStatus) !== undefined
        ? objectStatus.find((x) => errorStatus && x.status === errorStatus).comment
        : "";
    if (adamStatus === "livr" && currentStatus === ObjectStatus.Delivered)
      return objectStatus.find((x) => errorStatus && x.status === errorStatus) !== undefined
        ? objectStatus.find((x) => errorStatus && x.status === errorStatus).comment
        : "";
    if (
      (ObjectStatus.NeedCheckOrEditing === currentStatus || ObjectStatus.RejectedAuto === currentStatus) &&
      adamStatus !== "hab"
    )
      return "";
    if (
      currentStatus === ObjectStatus.Rejected &&
      (adamStatus === "pad" || adamStatus === "livr" || adamStatus === "diff")
    )
      return "";
    if (currentStatus === ObjectStatus.RejectedForBroadcast && (adamStatus === "livr" || adamStatus === "diff"))
      return "";
    return (
      (currentStatus === warningStatus
        ? objectStatus.find((x) => x.status === warningStatus) !== undefined
          ? objectStatus.find((x) => x.status === warningStatus).comment
          : ""
        : objectStatus.find((x) => x.status === validStatus) !== undefined
        ? objectStatus.find((x) => x.status === validStatus).comment
        : objectStatus.find((x) => errorStatus && x.status === errorStatus) !== undefined
        ? objectStatus.find((x) => errorStatus && x.status === errorStatus).comment
        : "") + (formatValue ? " - " + formatValue.join(" ") : "")
    );
  };

  return (
    <Tooltip title={getFinalTootip()}>
      <Box
        alignItems={"center"}
        width={"100%"}
        display={"flex"}
        flexDirection={"column"}
        p={0.5}
        sx={{ backgroundColor: formatValue && getFinalColor() }}
        borderRadius={"6px"}
        minWidth={"64px"}
      >
        {formatValueNoYear
          ? formatValueNoYear.map((x, i) => (
              <Typography
                color={theme.palette.getContrastText(getFinalColor())}
                key={i}
                fontWeight={400}
                variant={i === 0 ? "body1" : "body2"}
                sx={{ opacity: i === 0 ? 1 : 0.7 }}
              >
                {x}
              </Typography>
            ))
          : "-"}
      </Box>
    </Tooltip>
  );
};
